/**
 * Helper utilities for Microsoft Clarity integration
 */

let clarityReadyPromise = null

/**
 * Wait for Clarity to be loaded and ready
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 5000)
 * @returns {Promise<boolean>} - Resolves to true when Clarity is ready, false if timeout
 */
export const waitForClarity = (timeout = 5000) => {
    if (clarityReadyPromise) {
        return clarityReadyPromise
    }

    clarityReadyPromise = new Promise((resolve) => {
        const startTime = Date.now()

        const checkClarity = () => {
            if (typeof window.clarity === 'function') {
                resolve(true)
                return
            }

            if (Date.now() - startTime > timeout) {
                resolve(false)
                return
            }

            // Check again in 100ms
            setTimeout(checkClarity, 100)
        }

        checkClarity()
    })

    return clarityReadyPromise
}

/**
 * Get Clarity session URL with automatic retry
 * @returns {Promise<string|null>} - The Clarity session URL or null if not available
 */
export const getClaritySessionUrl = async () => {
    // First ensure Clarity is loaded
    const isLoaded = await waitForClarity()

    if (!isLoaded || typeof window.clarity !== 'function') {
        return null
    }

    return new Promise((resolve) => {
        const timeout = setTimeout(() => {
            resolve(null)
        }, 1000)

        try {
            window.clarity('metadata', function (metadata) {
                clearTimeout(timeout)
                if (metadata && metadata.projectId && metadata.userId && metadata.sessionId) {
                    const url =
                        'https://clarity.microsoft.com/player/' +
                        metadata.projectId +
                        '/' +
                        metadata.userId +
                        '/' +
                        metadata.sessionId
                    resolve(url)
                } else {
                    resolve(null)
                }
            })
        } catch (error) {
            clearTimeout(timeout)
            resolve(null)
        }
    })
}

/**
 * Initialize Clarity tracking when ready
 * Useful for setting up event listeners or initial tracking
 */
export const onClarityReady = (callback) => {
    waitForClarity().then((isReady) => {
        if (isReady && callback) {
            callback()
        }
    })
}

// Store Clarity session info globally once loaded
let cachedClarityInfo = null

export const getCachedClarityInfo = () => cachedClarityInfo

// Auto-cache Clarity info when it becomes available
if (typeof window !== 'undefined') {
    onClarityReady(() => {
        getClaritySessionUrl().then((url) => {
            if (url) {
                cachedClarityInfo = {
                    url,
                    timestamp: new Date().toISOString(),
                }
            }
        })
    })
}
